import { useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ListViewColumnConfig } from '@/lib/views/listViewSchemas/types';
import type { AdvancedFilterRow } from './useAdvancedTableState';
import { generateFilterId } from './useAdvancedTableState';
import { AdvancedFilterRowComponent } from './AdvancedFilterRow';
import { DEFAULT_ADVANCED_TABLE_LABELS, type AdvancedTableLabels } from './advancedTableLabels';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AdvancedFilterListProps {
  columns: ListViewColumnConfig[];
  filterRows: AdvancedFilterRow[];
  setFilterRows: (rows: AdvancedFilterRow[]) => void;
  isOpen: boolean;
  labels?: Partial<AdvancedTableLabels>;
  /** Pre-fetched select options per fieldname */
  selectOptions?: Record<string, { label: string; value: string }[]>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdvancedFilterList({
  columns,
  filterRows,
  setFilterRows,
  isOpen,
  labels,
  selectOptions,
}: AdvancedFilterListProps) {
  const l = { ...DEFAULT_ADVANCED_TABLE_LABELS, ...labels };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleAddFilter = useCallback(() => {
    const newRow: AdvancedFilterRow = {
      id: generateFilterId(),
      field: '',
      operator: 'equals',
      value: null,
    };
    setFilterRows([...filterRows, newRow]);
  }, [filterRows, setFilterRows]);

  const handleRemoveFilter = useCallback(
    (id: string) => {
      const next = filterRows.filter((r) => r.id !== id);
      setFilterRows(next);
    },
    [filterRows, setFilterRows],
  );

  const handleChangeFilter = useCallback(
    (id: string, updates: Partial<AdvancedFilterRow>) => {
      const next = filterRows.map((r) =>
        r.id === id ? { ...r, ...updates } : r,
      );
      setFilterRows(next);
    },
    [filterRows, setFilterRows],
  );

  const handleClearAll = useCallback(() => {
    setFilterRows([]);
  }, [setFilterRows]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = filterRows.findIndex((r) => r.id === active.id);
      const newIndex = filterRows.findIndex((r) => r.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      setFilterRows(arrayMove(filterRows, oldIndex, newIndex));
    },
    [filterRows, setFilterRows],
  );

  if (!isOpen) return null;

  return (
    <div
      className="rounded border border-border/40 bg-muted/30 px-2 py-2"
      role="region"
      aria-label="Filter rules"
    >
      {filterRows.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={filterRows.map((r) => r.id)}
            strategy={verticalListSortingStrategy}
          >
            {filterRows.map((row, index) => (
              <AdvancedFilterRowComponent
                key={row.id}
                row={row}
                index={index}
                columns={columns}
                onChange={handleChangeFilter}
                onRemove={handleRemoveFilter}
                labels={labels}
                selectOptions={selectOptions}
              />
            ))}
          </SortableContext>
        </DndContext>
      ) : (
        <p className="px-2 py-1 text-xs text-muted-foreground">
          {l.filterNoFilters}
        </p>
      )}

      {/* Actions row */}
      <div className="mt-1 flex items-center justify-between px-2">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 text-xs"
          onClick={handleAddFilter}
        >
          <Plus className="h-3 w-3" />
          {l.filterAddFilter}
        </Button>
        {filterRows.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground hover:text-destructive"
            onClick={handleClearAll}
          >
            {l.filterClearAll}
          </Button>
        )}
      </div>
    </div>
  );
}
